// 商户后台路由 —— 正式前缀 /merchant/*（PRD C1.3；菜单冻结表：7 组 23 项）
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastProvider } from '@/components/Toast';
import ErrorBoundary from '@/components/ErrorBoundary';
import OfflineBanner from '@/components/OfflineBanner';
import { setAuthFailureHandler, setPendingOnboardingHandler } from '@/api/client';
import Layout from '@/components/Layout';
import RequireAuth from '@/auth/RequireAuth';
import RequireSetup from '@/auth/RequireSetup';
import LoginPage from '@/pages/auth';
import OnboardPage from '@/pages/onboard';
// 企业 KYB 审核页静态引入：S9 切换 /corporate 新契约（F-007）——懒加载失败不得静默重定向 dashboard
// （旧 /corps 台账页 @deprecated 保留退役：pages/users/corporate-onboarding.tsx）
import CorporateKybReviewPage from '@/pages/users/corporates/onboarding';
// 卡片中心重组（S5-04）：六条旧路由映射表与重定向目标构造
import { CARD_CENTER_LEGACY_ROUTES, resolveLegacyCardCenterRedirect } from '@/lib/cardCenterRoutes';
// C1.3 旧路由重定向：被 Tab 壳吸收的旧地址 → 壳基路径 + 对应 ?tab=（query 透传）
import { LEGACY_TAB_REDIRECTS, LegacyTabRedirect } from '@/routes/legacyRedirects';

const DashboardPage = lazy(() => import('@/pages/dashboard'));
// 商户本人 KYB 自助页（qa-fix-n1n10 US-001 · FIX-001）：admin 直建 PENDING 商户的开户资料出口，
// 注册于 RequireAuth→RequireSetup 内（认证内独立页，不混用公开代理向导 /onboard）
const OnboardingApplicationPage = lazy(() => import('@/pages/onboarding-application'));
// PAGE-MCH-USER-015 审批中心（独立一级菜单 /merchant/approvals，五类型待办聚合）
const ApprovalsCenterPage = lazy(() => import('@/pages/approvals'));
const UsersPage = lazy(() => import('@/pages/users'));
const InvitesPage = lazy(() => import('@/pages/users/invites'));
// PAGE-MCH-USER-009 企业用户列表（S9：ONB-006 企业客户 + 待审队列入口）
const CorporateUsersPage = lazy(() => import('@/pages/users/corporates'));
// V7.3 T1：PAGE-010~014 企业用户五页（企业详情/消费审批/预算与分类/账单报销/风控配置）
const CorporateDetailPage = lazy(() => import('@/pages/users/corporates/detail'));
const CorporateApprovalsPage = lazy(() => import('@/pages/users/corporates/approvals'));
const CorporateBudgetsPage = lazy(() => import('@/pages/users/corporates/budgets'));
const CorporateBillingPage = lazy(() => import('@/pages/users/corporates/billing'));
const CorporateRiskPage = lazy(() => import('@/pages/users/corporates/risk'));
const CorporateMembersPage = lazy(() => import('@/pages/users/corporates/members'));
const CorporateCardsPage = lazy(() => import('@/pages/users/corporates/cards'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
// 运营代登录落地页（公开路由，无 JWT；与 agent 门户同构）
const PortalLoginPage = lazy(() => import('@/pages/portal-login'));
const RatesPage = lazy(() => import('@/pages/channel/rates'));
// 卡片中心（静态 path 须排在 card/:cardId 之前，避免被动态段吞掉）
const CardListPage = lazy(() => import('@/pages/card/list'));
// S5-04 合并页：新卡管理（014，C1.3 增挂实体卡预申请/流转两 Tab）/ 交易管理（015）
const CardIssueCenterPage = lazy(() => import('@/pages/card/issue-center'));
const CardTransactionCenterPage = lazy(() => import('@/pages/card/transaction-center'));
const CardPendingActivationPage = lazy(() => import('@/pages/card/pending-activation'));
const CardTransferPage = lazy(() => import('@/pages/card/transfer'));
const CardDetailPage = lazy(() => import('@/pages/card/detail'));
// S5-05 需求8 佣金管理合并：三佣金路由共用壳页（壳内 Tab 由路由解析并 lazy 挂载既有三页）
const CommissionCenterPage = lazy(() => import('@/pages/finance/commission-center'));
const PnlPage = lazy(() => import('@/pages/finance/pnl'));
const CreditsPage = lazy(() => import('@/pages/finance/credits'));
const CreditNewPage = lazy(() => import('@/pages/finance/credits/new'));
// C1.3 Tab 壳（纯壳：?tab= 持态 + lazy 挂载既有页面组件，业务页零改动）
const ChannelCenterShell = lazy(() => import('@/pages/shells/ChannelCenterShell'));
const KycCenterShell = lazy(() => import('@/pages/shells/KycCenterShell'));
const RiskRadarShell = lazy(() => import('@/pages/shells/RiskRadarShell'));
const RiskRulesShell = lazy(() => import('@/pages/shells/RiskRulesShell'));
const FinanceChannelShell = lazy(() => import('@/pages/shells/FinanceChannelShell'));
const FinanceUsersShell = lazy(() => import('@/pages/shells/FinanceUsersShell'));
const SettingsAccountShell = lazy(() => import('@/pages/shells/SettingsAccountShell'));
const SettingsApiKeysShell = lazy(() => import('@/pages/shells/SettingsApiKeysShell'));
const SettingsEmailShell = lazy(() => import('@/pages/shells/SettingsEmailShell'));
const SettingsSecurityShell = lazy(() => import('@/pages/shells/SettingsSecurityShell'));
// 3DS 管理（V84 · 商户自定义 3DS 接收邮箱：绑定验证 + 换绑 + 解绑回落登录邮箱）
const ThreeDsEmailPage = lazy(() => import('@/pages/settings/three-ds'));
// KYC 审核队列别名直挂（users/kyc-review 与壳并存：PRD 别名 URL 不变，勿 Navigate 丢掉 URL）
const KycPage = lazy(() => import('@/pages/kyc'));
const MembersPage = lazy(() => import('@/pages/settings/members'));
// 消息中心收件箱（需求9 · PAGE-MCH-NTF-001 · /merchant/messages；P-003：一级菜单出组，铃铛接管入口）
const MessagesPage = lazy(() => import('@/pages/messages'));
// 个人用户页（S5 · US-S5-08：users/individuals 换绑 element，D6 两路由并存、URL 不变语义升级）
const IndividualUsersPage = lazy(() => import('@/pages/users/individuals'));

function PageFallback() {
  return <div className="p-10 text-center text-sm text-slate-400">加载页面…</div>;
}

/**
 * S5-04 旧路由兼容（PAGE-MCH-CARD-014/015 重定向）：
 * 六条旧地址经映射表 <Navigate replace> 至合并页对应 Tab，语义等同 301；
 * 业务 query 原样透传（tab 由映射表覆盖写入）。
 */
function CardCenterLegacyRedirect({ legacyPath }: { legacyPath: string }) {
  const { search } = useLocation();
  const target = resolveLegacyCardCenterRedirect(legacyPath, search);
  if (!target) {
    // 映射表未收录（理论不可达，防御兜底）：回退卡片列表
    return <Navigate to="/merchant/card/list" replace />;
  }
  return <Navigate to={`${target.pathname}${target.search}`} replace />;
}

const LEGACY: { from: string; to: string }[] = [
  { from: 'dashboard', to: '/merchant/dashboard' },
  { from: 'channel/topup', to: '/merchant/channel?tab=topup' },
  { from: 'channel/rates', to: '/merchant/channel?tab=rates' },
  { from: 'channel/fee-items', to: '/merchant/channel?tab=fee-items' },
  { from: 'channel/physical-apply', to: '/merchant/card/issue-center?tab=PHYSICAL_APPLY' },
  { from: 'channel/physical-cards', to: '/merchant/card/issue-center?tab=PHYSICAL_CARDS' },
  { from: 'users', to: '/merchant/users' },
  { from: 'kyc', to: '/merchant/kyc' },
  { from: 'kyc/fill', to: '/merchant/kyc?tab=fill' },
  { from: 'kyc/fines', to: '/merchant/kyc?tab=fines' },
  { from: 'business/virtual-cards', to: '/merchant/card/issue-center?tab=SINGLE_VIRTUAL' },
  { from: 'business/physical-issue', to: '/merchant/card/issue-center?tab=SINGLE_PHYSICAL' },
  { from: 'business/recharge-config', to: '/merchant/finance/channel?tab=recharge-config' },
  { from: 'business/deposits', to: '/merchant/finance/users?tab=deposits' },
  { from: 'finance/channel', to: '/merchant/finance/channel' },
  { from: 'finance/users', to: '/merchant/finance/users' },
  { from: 'finance/commissions', to: '/merchant/finance/commissions' },
  { from: 'finance/settlements', to: '/merchant/finance/settlements' },
  { from: 'finance/invoices', to: '/merchant/finance/invoices' },
  { from: 'brand', to: '/merchant/settings/account?tab=brand' },
  { from: 'settings/account', to: '/merchant/settings/account' },
  { from: 'settings/payment', to: '/merchant/settings/security' },
  { from: 'settings/security', to: '/merchant/settings/security' },
  { from: 'settings/logs', to: '/merchant/settings/security?tab=logs' },
  { from: 'settings/api-keys', to: '/merchant/settings/api-keys' },
  { from: 'settings/webhooks', to: '/merchant/settings/api-keys?tab=webhooks' },
];

/** 路由内部：注册 401 鉴权失败回调 → navigate('/login') */
function AppRoutes() {
  const navigate = useNavigate();
  useEffect(() => {
    setAuthFailureHandler(() => navigate('/login'));
    // 3417（PENDING 开户受限）全局引导：业务 API 命中即送 KYB 自助页
    setPendingOnboardingHandler(() => navigate('/merchant/onboarding-application'));
    return () => {
      setAuthFailureHandler(null);
      setPendingOnboardingHandler(null);
    };
  }, [navigate]);

  return (
    <Routes>
      {/* 公开路由 */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* 运营代登录落地页：无 JWT，必须在 RequireAuth 之外 */}
      <Route path="/portal-login" element={<PortalLoginPage />} />
      {/* 代理专属开户向导：无 JWT，必须在 RequireAuth 之外 */}
      <Route path="/onboard" element={<OnboardPage />} />

      {/* 受保护路由（RequireAuth：无 token → /login；RequireSetup：强制改密/绑 2FA） */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireSetup />}>
        <Route path="/" element={<Navigate to="/merchant/dashboard" replace />} />
        <Route path="/merchant" element={<Layout />}>
              <Route index element={<Navigate to="dashboard" replace />} />

              <Route path="dashboard" element={<DashboardPage />} />

              {/* 商户本人 KYB 自助（qa-fix-n1n10 US-001）：静态段排在前，3417 引导落点 */}
              <Route path="onboarding-application" element={<OnboardingApplicationPage />} />

              {/* 审批中心（PAGE-MCH-USER-015）：独立一级菜单，静态段置于 * splat 之前 */}
              <Route path="approvals" element={<ApprovalsCenterPage />} />

              {/* 通道业务壳（C1.3 冻结表）：topup|rates|fee-items 三 Tab；
                  旧三子路由与实体卡两路由经 LEGACY_TAB_REDIRECTS 重定向 */}
              <Route path="channel" element={<ChannelCenterShell />} />
              {/* PRD 正式路由（PAGE-MCH-FIN-010 指定 /merchant/finance/rates）：别名直挂不变 */}
              <Route path="finance/rates" element={<RatesPage />} />

              <Route path="users" element={<UsersPage />} />
              <Route path="users/invites" element={<InvitesPage />} />
              {/* S5 换绑（US-S5-08）：element 由 UsersPage 换为 IndividualUsersPage
                  （D6 两路由并存、URL 不变语义升级；/merchant/users 用户管理原样保留、无重定向） */}
              <Route path="users/individuals" element={<IndividualUsersPage />} />
              {/* KYC 管理壳（C1.3）：review|fill|fines 三 Tab；旧 kyc/fill、kyc/fines 静态段
                  经 LEGACY_TAB_REDIRECTS 重定向（排在 kyc 之前语义由路由特异性保证） */}
              <Route path="kyc" element={<KycCenterShell />} />
              {/* 审核队列别名直挂：与壳并存，URL 不变（勿合并/勿重定向） */}
              <Route path="users/kyc-review" element={<KycPage />} />
              {/* 企业用户（S9 / PAGE-MCH-USER-009）：静态段排在 users/corporates/onboarding 之前 */}
              <Route path="users/corporates" element={<CorporateUsersPage />} />
              {/* 企业 KYB 审核（S9 / PAGE-MCH-USER-004 扩展，ONB-001/002/005 新契约）：
                  静态段置于动态 :corpId 之前 */}
              <Route path="users/corporates/onboarding" element={<CorporateKybReviewPage />} />
              {/* V7.3 T1（PAGE-010~014）：企业详情域五页；动态 :corpId 排在静态 onboarding 之后 */}
              <Route path="users/corporates/:corpId" element={<CorporateDetailPage />} />
              <Route path="users/corporates/:corpId/members" element={<CorporateMembersPage />} />
              <Route path="users/corporates/:corpId/cards" element={<CorporateCardsPage />} />
              <Route path="users/corporates/:corpId/approvals" element={<CorporateApprovalsPage />} />
              <Route path="users/corporates/:corpId/budgets" element={<CorporateBudgetsPage />} />
              <Route path="users/corporates/:corpId/billing" element={<CorporateBillingPage />} />
              <Route path="users/corporates/:corpId/risk" element={<CorporateRiskPage />} />
              {/* S5-04 旧路由兼容：开卡（虚拟/实体）两旧址重定向至新卡管理对应 Tab（合并页零能力丢失） */}
              {CARD_CENTER_LEGACY_ROUTES.filter((r) => r.legacyPath.startsWith('business/')).map(({ legacyPath }) => (
                <Route key={legacyPath} path={legacyPath} element={<CardCenterLegacyRedirect legacyPath={legacyPath} />} />
              ))}

              {/* 卡片中心：静态 path 必须写在 card/:cardId 之前 */}
              <Route path="card/list" element={<CardListPage />} />
              {/* S5-04 合并页：新卡管理（PAGE-MCH-CARD-014，C1.3 增挂实体卡两 Tab）/
                  交易管理（PAGE-MCH-CARD-015），?tab= 持态 */}
              <Route path="card/issue-center" element={<CardIssueCenterPage />} />
              <Route path="card/transaction-center" element={<CardTransactionCenterPage />} />
              {/* S5-04 旧路由兼容：批量开卡/开卡进度/卡片交易/账单四旧址重定向至合并页对应 Tab */}
              {CARD_CENTER_LEGACY_ROUTES.filter((r) => !r.legacyPath.startsWith('business/')).map(({ legacyPath }) => (
                <Route key={legacyPath} path={legacyPath} element={<CardCenterLegacyRedirect legacyPath={legacyPath} />} />
              ))}
              <Route path="card/pending-activation" element={<CardPendingActivationPage />} />
              <Route path="card/:cardId/transfer" element={<CardTransferPage />} />
              <Route path="card/:cardId" element={<CardDetailPage />} />

              {/* 财务壳（C1.3）：通道资金吸收充值配置、用户资金吸收用户充值（旧 business/* 路由重定向） */}
              <Route path="finance/channel" element={<FinanceChannelShell />} />
              <Route path="finance/users" element={<FinanceUsersShell />} />
              {/* S5-05 需求8 佣金管理合并：三路由路径不变，element 指向壳页（Tab 由壳内路由解析） */}
              <Route path="finance/commissions" element={<CommissionCenterPage />} />
              <Route path="finance/settlements" element={<CommissionCenterPage />} />
              <Route path="finance/pnl" element={<PnlPage />} />
              <Route path="finance/invoices" element={<CommissionCenterPage />} />
              {/* US-025 划转记录 / 划转发起（PAGE-MCH-FIN-009 / PAGE-MCH-FIN-008） */}
              <Route path="finance/credits" element={<CreditsPage />} />
              <Route path="finance/credits/new" element={<CreditNewPage />} />
              {/* PRD 正式别名（目标即壳基路径/佣金壳，天然兼容） */}
              <Route path="finance/channel-funds" element={<Navigate to="/merchant/finance/channel" replace />} />
              <Route path="finance/user-funds" element={<Navigate to="/merchant/finance/users" replace />} />
              <Route path="finance/withdrawals" element={<Navigate to="/merchant/finance/settlements" replace />} />

              {/* 风控壳（C1.3）：风险雷达吸收用户风险、规则与处置吸收快捷处置
                  （actions Tab 带读权限闸门，无权限深链由壳内回退首 Tab）；
                  risk/users、risk/quick-actions 旧路由经 LEGACY_TAB_REDIRECTS 重定向 */}
              <Route path="risk/radar" element={<RiskRadarShell />} />
              <Route path="risk/rules" element={<RiskRulesShell />} />

              {/* 设置壳（C1.3）：账户与品牌吸收 brand；API 与集成吸收 webhooks/developer-docs；
                  通知与邮件吸收 email-templates；安全与日志吸收 logs（旧路由重定向） */}
              <Route path="settings/account" element={<SettingsAccountShell />} />
              <Route path="settings/security" element={<SettingsSecurityShell />} />
              <Route path="settings/api-keys" element={<SettingsApiKeysShell />} />
              {/* 设置中心补全（S5 · US-S5-05/06/07）：成员管理独立页保留 */}
              <Route path="settings/members" element={<MembersPage />} />
              <Route path="settings/email-channels" element={<SettingsEmailShell />} />
              {/* 3DS 管理（V84）：独立页，不并入通知与邮件壳（收件人口径与通道配置无关） */}
              <Route path="settings/three-ds" element={<ThreeDsEmailPage />} />

              {/* 消息中心（需求9 · PAGE-MCH-NTF-001）：路由保留；P-003 一级菜单出组，Header 铃铛接管 */}
              <Route path="messages" element={<MessagesPage />} />

              {/* 营销中心（S5 · PAGE-MCH-MKT-*）：PRD 明文本期不做/不可达——路由已删除且不挂重定向，
                  页面文件保留在 pages/marketing/（后期启用）；直接访问落 * splat → dashboard */}

              {/* 被 Tab 壳吸收的旧路由统一重定向（C1.3 冻结表，query 原样透传） */}
              {LEGACY_TAB_REDIRECTS.map(({ from, to, tab }) => (
                <Route key={from} path={from} element={<LegacyTabRedirect to={to} tab={tab} />} />
              ))}

              <Route path="*" element={<Navigate to="/merchant/dashboard" replace />} />
        </Route>
        </Route>
      </Route>

      {LEGACY.map(({ from, to }) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}
      <Route path="*" element={<Navigate to="/merchant/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}
